import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { TypeAnimation } from 'react-type-animation';
import { FaGithub, FaLinkedinIn, FaTelegramPlane, FaWhatsapp, FaDownload } from 'react-icons/fa';
import { HiArrowDown } from 'react-icons/hi';
import { PERSON } from '../data';

/* ── Particle canvas ─────────────────────────────────── */
function Particles() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx   = canvas.getContext('2d');
    let raf;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    const COLS = ['139,92,246','99,102,241','34,211,238','167,139,250'];
    const pts  = Array.from({ length: 110 }, () => ({
      x:  Math.random() * canvas.width,
      y:  Math.random() * canvas.height,
      r:  Math.random() * 1.4 + 0.3,
      vx: (Math.random() - 0.5) * 0.28,
      vy: (Math.random() - 0.5) * 0.28,
      op: Math.random() * 0.45 + 0.1,
      c:  COLS[Math.floor(Math.random() * COLS.length)],
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pts.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width)  p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.c},${p.op})`;
        ctx.fill();
      });
      pts.forEach((a, i) => pts.slice(i + 1).forEach(b => {
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (d < 95) {
          ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
          ctx.strokeStyle = `rgba(124,58,237,${0.07 * (1 - d / 95)})`;
          ctx.lineWidth = 0.5; ctx.stroke();
        }
      }));
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full pointer-events-none" />;
}

const SOCIALS = [
  { Icon:FaGithub,       href:PERSON.github,   label:'GitHub'    },
  { Icon:FaLinkedinIn,   href:PERSON.linkedin, label:'LinkedIn'  },
  { Icon:FaTelegramPlane,href:PERSON.telegram, label:'Telegram'  },
  { Icon:FaWhatsapp,     href:PERSON.whatsapp, label:'WhatsApp'  },
];

const fade = (delay=0) => ({
  initial:{ opacity:0, y:24 },
  animate:{ opacity:1, y:0 },
  transition:{ duration:0.6, delay },
});

export default function Hero() {
  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Backgrounds */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#07070f] via-[#0d0716] to-[#07070f]" />
      <Particles />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-600/8 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyan-500/6 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-24 w-full">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-16">

          {/* ── Left ─────────────────────────────── */}
          <div className="flex-1 max-w-2xl">

            {/* Status badge */}
            <motion.div {...fade(0)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-violet-500/20 text-sm text-violet-300 font-medium mb-8"
            >
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              Available for new projects
            </motion.div>

            {/* Name */}
            <motion.h1 {...fade(0.1)} className="text-5xl sm:text-6xl lg:text-7xl font-black leading-tight tracking-tight mb-1 text-white">
              Hi, I'm
            </motion.h1>
            <motion.h1 {...fade(0.15)} className="text-5xl sm:text-6xl lg:text-7xl font-black leading-tight tracking-tight mb-6 grad-text">
              Mustafa Kadir
            </motion.h1>

            {/* Typewriter */}
            <motion.div {...fade(0.2)} className="flex flex-wrap items-center gap-2 mb-8">
              <span className="text-slate-400 text-lg font-light">I build</span>
              <span className="text-xl sm:text-2xl font-bold text-cyan-400 font-mono">
                <TypeAnimation
                  sequence={['Enterprise Systems',2000,'React Applications',2000,'Laravel APIs',2000,'HRMS Platforms',2000,'ERP Solutions',2000]}
                  speed={50} repeat={Infinity}
                />
              </span>
            </motion.div>

            {/* Bio */}
            <motion.p {...fade(0.25)} className="text-slate-400 text-lg leading-relaxed mb-10 max-w-lg">
              Senior Full-Stack Developer with{' '}
              <span className="text-violet-400 font-semibold">7+ years</span>{' '}
              crafting enterprise-grade applications — from HRMS to ERP platforms, built with React and Laravel,Python .
            </motion.p>

            {/* CTAs */}
            <motion.div {...fade(0.3)} className="flex flex-wrap gap-4 mb-12">
              <motion.a href="#contact" whileHover={{scale:1.05,y:-2}} whileTap={{scale:0.97}}
                className="px-8 py-4 text-base font-bold text-white rounded-xl btn-primary neon-v inline-flex items-center gap-2">
                Hire Me <span className="text-cyan-300">✦</span>
              </motion.a>
              <motion.a href="#projects" whileHover={{scale:1.05,y:-2}} whileTap={{scale:0.97}}
                className="px-8 py-4 text-base font-bold text-white rounded-xl btn-ghost">
                View Projects
              </motion.a>
              <motion.a href={PERSON.cv} download whileHover={{scale:1.05,y:-2}} whileTap={{scale:0.97}}
                className="px-6 py-4 text-sm font-semibold text-slate-300 hover:text-white rounded-xl glass transition-all inline-flex items-center gap-2">
                <FaDownload size={13}/> CV
              </motion.a>
            </motion.div>

            {/* Socials */}
            <motion.div {...fade(0.35)} className="flex items-center gap-4">
              <span className="text-slate-600 text-sm">Connect:</span>
              {SOCIALS.map(({ Icon, href, label }, i) => (
                <motion.a key={label} href={href} target="_blank" rel="noopener noreferrer"
                  initial={{opacity:0,scale:0}} animate={{opacity:1,scale:1}}
                  transition={{delay:0.55+i*0.08,type:'spring'}}
                  whileHover={{scale:1.2,y:-3}}
                  className="w-10 h-10 rounded-lg glass flex items-center justify-center text-slate-400 hover:text-violet-400 border border-white/5 hover:border-violet-500/40 transition-all duration-200"
                  title={label}
                >
                  <Icon size={15}/>
                </motion.a>
              ))}
            </motion.div>
          </div>

          {/* ── Right — Avatar ─────────────────────── */}
          <motion.div
            initial={{ opacity:0, scale:0.85, x:40 }}
            animate={{ opacity:1, scale:1,    x:0  }}
            transition={{ duration:0.8, delay:0.3, type:'spring', stiffness:80 }}
            className="relative flex-shrink-0"
          >
            <div className="relative w-64 h-64 sm:w-72 sm:h-72 lg:w-88 lg:h-88">
              {/* Orbiting rings */}
              <motion.div animate={{rotate:360}} transition={{duration:22,repeat:Infinity,ease:'linear'}}
                className="absolute inset-0 rounded-full border border-dashed border-violet-500/20" />
              <motion.div animate={{rotate:-360}} transition={{duration:16,repeat:Infinity,ease:'linear'}}
                className="absolute inset-4 rounded-full border border-dashed border-cyan-500/15" />

              {/* Photo placeholder */}
              <div className="absolute inset-8 rounded-full bg-gradient-to-br from-violet-600/30 to-indigo-900/50 border border-violet-500/30 flex flex-col items-center justify-center neon-v">
                <span className="text-5xl">👨‍💻</span>
                <span className="text-violet-300 text-xs font-mono mt-1">Full-Stack Dev</span>
              </div>

              {/* Floating badges */}
              <motion.div animate={{y:[-5,5,-5]}} transition={{duration:3,repeat:Infinity,ease:'easeInOut'}}
                className="absolute -top-4 -right-4 glass border border-violet-500/30 rounded-xl px-3 py-1.5 text-xs font-bold text-violet-300 whitespace-nowrap">
                ⚛️ React Expert
              </motion.div>
              <motion.div animate={{y:[5,-5,5]}} transition={{duration:4,repeat:Infinity,ease:'easeInOut',delay:1}}
                className="absolute -bottom-4 -left-4 glass border border-cyan-500/30 rounded-xl px-3 py-1.5 text-xs font-bold text-cyan-300 whitespace-nowrap">
                🚀 7+ Years
              </motion.div>
              <motion.div animate={{x:[-4,4,-4]}} transition={{duration:5,repeat:Infinity,ease:'easeInOut',delay:0.5}}
                className="absolute top-1/2 -right-12 glass border border-indigo-500/30 rounded-xl px-3 py-1.5 text-xs font-bold text-indigo-300 whitespace-nowrap">
                🔴 Laravel
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Scroll cue */}
        <motion.div initial={{opacity:0}} animate={{opacity:1}} transition={{delay:1.3}}
          className="flex justify-center mt-16 pb-8">
          <motion.a href="#about" animate={{y:[0,8,0]}} transition={{duration:2,repeat:Infinity}}
            className="flex flex-col items-center gap-2 text-slate-500 hover:text-violet-400 transition-colors">
            <span className="text-xs font-mono tracking-widest uppercase">Scroll</span>
            <HiArrowDown size={17}/>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
}
